// =============================================================================
// src/data/manaus-getters.ts — Getters puros do DATA core (sem efeito colateral).
//
// Proveniencia: assets/unified-app.js DATA._simpleHash / DATA.getMunData /
//   DATA.getMunPop / DATA.pickColor (linhas 274-307). Portado FIEL: a logica de
//   hash, fallback e selecao de perfil reproduz exatamente o original para
//   manter paridade visual nas camadas de municipio. NAO "corrigir" — replicar.
// =============================================================================

import type {
  MunicipalityId,
  ElectoralYear,
  ZoneElectoralResult,
  MunicipalityPopDisplay,
  HexColor,
} from '../app/types';
import {
  MANAUS_MUN_POP,
  MANAUS_URBAN_IDS,
  MANAUS_PROFILE_PARTIES,
} from './manaus-municipalities';

/** Id IBGE da capital Manaus — desvia o perfil para 'capital' em getMunData. */
const MANAUS_CAPITAL_ID: MunicipalityId = '1302603';

/**
 * Hash deterministico simples de string (DATA._simpleHash).
 * Replica o algoritmo `((h << 5) - h) + charCode`, com `| 0` por iteracao para
 * forcar overflow de 32 bits identico ao original; usado so para distribuir o
 * perfil 'alt' de forma estavel entre municipios.
 */
export function simpleHashOf(text: string): number {
  let accumulator = 0;
  for (let index = 0; index < text.length; index++) {
    accumulator = (accumulator << 5) - accumulator + text.charCodeAt(index);
    accumulator |= 0;
  }
  return Math.abs(accumulator);
}

/** Classifica o municipio em capital/urban/rural (replica DATA.getMunData). */
function profileOf(munId: MunicipalityId): 'capital' | 'urban' | 'rural' {
  if (munId === MANAUS_CAPITAL_ID) return 'capital';
  return MANAUS_URBAN_IDS.indexOf(munId) >= 0 ? 'urban' : 'rural';
}

/**
 * Resultado eleitoral mock de um municipio num ano (DATA.getMunData).
 * Nao-capitais cujo hash(id+ano) % 4 === 0 recebem o perfil 'alt' — variacao
 * deterministica do original preservada. Default de ano: '2024'.
 */
export function getMunData(
  munId: MunicipalityId,
  year?: ElectoralYear,
): ZoneElectoralResult {
  const targetYear: ElectoralYear = year ?? '2024';
  const profile = profileOf(munId);
  const byProfile = MANAUS_PROFILE_PARTIES[targetYear];
  if (profile !== 'capital' && simpleHashOf(munId + targetYear) % 4 === 0) {
    return byProfile.alt;
  }
  return byProfile[profile];
}

/** Formata milhares -> "2,2 mi"/"320 mil" (replica o ramo `>= 1000` de getMunPop). */
function formatPopThousands(thousands: number): string {
  if (thousands >= 1000) {
    return (thousands / 1000).toFixed(1).replace('.', ',') + ' mi';
  }
  return thousands + ' mil';
}

/**
 * Populacao formatada de um municipio (DATA.getMunPop). Ids ausentes em munPop
 * caem no fallback fixo "~20 mil / ~12 mil" exatamente como o original.
 */
export function getMunPop(munId: MunicipalityId): MunicipalityPopDisplay {
  const raw = MANAUS_MUN_POP[munId];
  if (raw) {
    return { hab: formatPopThousands(raw[0]), eleitores: formatPopThousands(raw[1]) };
  }
  return { hab: '~20 mil', eleitores: '~12 mil' };
}

/**
 * Escolhe a cor de uma rampa por interpolacao normalizada (DATA.pickColor).
 * Mantem o clamp [0,1] e o `Math.min(idx, len-1)` do original. Com
 * noUncheckedIndexedAccess o acesso pode ser undefined em rampa vazia — guarda
 * defensiva adicionada (ver notesAndDeviations); rampas reais nunca sao vazias.
 */
export function pickColorFromRamp(
  value: number,
  min: number,
  max: number,
  ramp: readonly HexColor[],
): HexColor {
  const normalized = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const rampIndex = Math.min(Math.floor(normalized * ramp.length), ramp.length - 1);
  const color = ramp[rampIndex];
  if (color === undefined) {
    throw new Error(
      `pickColorFromRamp: rampa vazia recebida ${JSON.stringify(ramp)}, esperado >= 1 cor`,
    );
  }
  return color;
}
